import { humanize } from '@utils';
import { metricKeys } from '@pulse/metrics';
import moment from 'moment';
import { toNumber } from 'lodash';
import { lineChartReqBody as diagReqBody } from '@components/metrics-panel-hz/utils/metricCardsReqBodyTemplate';
import { DATASET, EXPORT_VM } from '@utils/constants';
import { GEO_DATA_METRICS, GEO_ERROR_METRICS } from './mappings';
import { GEO_CITY, SESSION } from './constants';
import { getTextColor } from '@components/overview-dashboard/metrics-container/spi-widget/SPIMetricsText';
import { columnValueFormatter } from '../SubscribersTable/utils';

const checkAdvancedMetricsEnabled = (supportedMetrics) => {
  const res = metricKeys.ConnectionInducedRebufferingRatio in supportedMetrics;
  return res;
};

const buildFilter = (viewerID) => {
  const baseFilter = {
    filter: [
      [
        {
          op: 'eq',
          field: 'VIEWER_ID',
          value: 'john@smith.com',
          key: 'VIEWER_ID',
          display: 'john@smith.com',
        },
      ],
    ],
  };
  baseFilter.filter[0][0].value = viewerID;
  return baseFilter.filter;
};

const buildSessionFilter = (viewerId) => {
  const baseFilter = {
    type: 'and',
    filters: [
      {
        type: 'or',
        filters: [
          {
            type: 'equal',
            dimension: 'VIEWER_ID',
            value: viewerId,
          },
        ],
      },
    ],
  };
  return baseFilter;
};

const buildInterval = (interval) => {
  let timeInterval: any = '';
  const { startDate, endDate, days, isCustom } = interval;
  const hoursTosubtract = Number(days) * 24;
  if (isCustom) {
    timeInterval = `${moment(startDate).format()}/${moment(endDate).endOf('day').format()}`;
  } else {
    timeInterval = `${moment()
      .subtract(hoursTosubtract, 'hours')
      //.startOf("hour") // for 24h window the round up creates >24h interval and affects granularity
      .format()}/${moment().format()}`;
  }
  return timeInterval;
};

const buildViewerExperienceFilter = (type, data) => {
  if (type == SESSION) {
    return {
      type: 'and',
      filters: [
        {
          type: 'equal',
          dimension: 'VIEWER_ID',
          value: data.viewerId,
        },
        {
          type: 'equal',
          dimension: 'SESSION_ID',
          value: data.sessionId,
        },
      ],
    };
  } else if (type == GEO_CITY) {
    return {
      type: 'equal',
      dimension: 'GEO_CITY',
      value: data.city,
    };
  } else {
    return {
      type: 'equal',
      dimension: 'GEO_ASN',
      value: `${data.asn}`,
    };
  }
};

const buildSessionRequestBody = ({ viewerId, interval, columns, limit = 200, isLive, page }) => {
  const queryInterval = buildInterval(interval);
  const body = {
    columns,
    thresholds: {
      VideoStartupTimeMilliSec: 10000,
      SessionTimeMilliSec: 10000,
      BitrateKbps: 200,
      TabletBitrateKbps: 400,
      TVBitrateKbps: 800,
      ConnectionInducedRebufferingRatio: 0.4,
      ConnectionInducedRebufferingTimeMilliSec: 2000,
    },
    filter: buildSessionFilter(viewerId),
    page,
    limit: isLive ? 100 : limit,
    locationStats: true,
    isLive,
  };
  // Live query does not require interval
  if (!isLive) {
    body['interval'] = queryInterval;
  }
  return body;
};

const buildRequestBody = (viewerID, interval, metricList) => {
  const body = JSON.parse(JSON.stringify(diagReqBody));
  body.queries[0].metrics = [...metricList];
  body.queries[0].filter = buildFilter(viewerID);
  body.queries[0].interval = buildInterval(interval);
  body.queries[0].granularity = 'ALL';
  body.queries[0].options = {
    ...body.queries[0].options,
    type: 'viewer-module',
  };
  return body;
};

const buildAdvanceRequestBody = (filterData, interval, metricList, type) => {
  const body = JSON.parse(JSON.stringify(diagReqBody));
  body.queries[0].metrics = [...metricList];
  body.queries[0].filter = buildViewerExperienceFilter(type, filterData);
  body.queries[0].interval = buildInterval(interval);
  body.queries[0].granularity = 'ALL';
  return body;
};

const buildSummary = (metricCategory, summaryData) => {
  const summary: any = {};
  for (const key in metricCategory) {
    if (key && summaryData) {
      if (!(key in summary)) {
        summary[key] = [];
      }
      metricCategory[key].map((metric) => {
        const value = summaryData[metric];
        summary[key].push({
          name: metric,
          value,
        });
      });
    }
  }
  return summary;
};

const buildLocationMetadata = (locationMetadata: any = {}) => {
  const location: any = [];
  const address = `${locationMetadata.city} , ${locationMetadata.state} , ${locationMetadata.country}`;
  const ispValue = `${locationMetadata.ispName} & ${locationMetadata.asnName}`;
  location.push(
    {
      name: 'Location',
      value: address,
    },
    {
      name: 'ISP & ASN',
      value: ispValue,
    },
    {
      name: 'IP',
      value: locationMetadata.ip,
    },
  );
  return location;
};

const getErrorMetric = (status) => {
  switch (status) {
    case 'EBVS':
      return metricKeys.ExitBeforeVideoStarts;
    case 'VSF':
      return metricKeys.VideoStartFailures;
    case 'VSF-T':
      return metricKeys.VideoStartFailuresTech;
    case 'VSF-B':
      return metricKeys.VideoStartFailuresBusiness;
    case 'VPF':
      return metricKeys.VideoPlaybackFailures;
    case 'VPF-T':
      return metricKeys.VideoPlaybackFailuresTech;
    case 'VPF-B':
      return metricKeys.VideoPlaybackFailuresBusiness;
    default:
      return 'NA';
  }
};

const buildViewingExpData = (sessionData, cityData, asnData, status) => {
  const tableData: any = [];
  const isErrorTable = ['VSF', 'VSF-T', 'VSF-B', 'EBVS'].includes(status);
  if (isErrorTable) {
    for (const m in GEO_ERROR_METRICS) {
      if (m) {
        const metric = GEO_ERROR_METRICS[m];
        if (metric === metricKeys.VideoStartFailures) {
          const isVSF = status !== 'EBVS';
          if (isVSF) {
            tableData.push({
              metrics: metric,
              session: 'Yes',
              city: columnValueFormatter(cityData[metric], metric),
              asn: columnValueFormatter(asnData[metric], metric),
            });
          } else {
            tableData.push({
              metrics: metric,
              session: 'No',
              city: columnValueFormatter(cityData[metric], metric),
              asn: columnValueFormatter(asnData[metric], metric),
            });
          }
        } else {
          tableData.push({
            metrics: metric,
            session: columnValueFormatter(sessionData[metric], metric),
            city: columnValueFormatter(cityData[metric], metric),
            asn: columnValueFormatter(asnData[metric], metric),
          });
        }
      }
    }
  } else {
    for (const m in GEO_DATA_METRICS) {
      if (m) {
        const metric = GEO_DATA_METRICS[m];
        tableData.push({
          metrics: metric,
          session: columnValueFormatter(sessionData[metric], metric),
          city: columnValueFormatter(cityData[metric], metric),
          asn: columnValueFormatter(asnData[metric], metric),
        });
      }
    }
  }

  return tableData;
};

const formatPlayTime = (value) => {
  if (value) {
    const seconds = value * 60;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor((seconds % 3600) % 60);
    const displayText = `${h}h ${m}m ${s}s`;
    return displayText;
  }
  return '';
};

const buildTagGroups = (locationMetadata, cdnMetadata, deviceTags, playerTags, otherTags) => {
  const tagGroups: any = [];
  tagGroups.push(
    {
      name: 'Location Metadata',
      tags: locationMetadata,
    },
    {
      name: 'CDN Metadata',
      tags: cdnMetadata,
    },
    {
      name: 'Device Metadata',
      tags: deviceTags,
    },
    {
      name: 'Player Metadata',
      tags: playerTags,
    },
    {
      name: 'Other Metadata',
      tags: otherTags,
    },
  );
  return tagGroups;
};

const getSharingState = (
  viewerId,
  interval,
  commonInterval,
  selectedCustomerId,
  selectedColumns,
) => ({
  title: 'VM Sharing',
  dashboard: 'vm',
  start: commonInterval.from,
  end: commonInterval.to,
  state: {
    common: {
      selectedCustomerId,
    },
    vmStore: {
      viewerId, // needed to recreate the VM sharing url in the dashboard tab
      interval,
      selectedColumns,
    },
  },
});

const getExportConfig = (viewerId, filterId, interval, metricList, selectedColumns, isLive) => {
  const columns = selectedColumns.map((a) => a.metric);
  const timeseries = buildRequestBody(viewerId, interval, metricList);
  const session = buildSessionRequestBody({ viewerId, interval, columns, isLive });
  return {
    dashboardType: EXPORT_VM,
    dataset: DATASET,
    state: {
      viewerId: viewerId && viewerId.toString(),
      timeseries,
      session,
    },
  };
};

const getSpiColor = (value: any) => {
  return getTextColor(humanize(toNumber(value), ''));
};

export {
  checkAdvancedMetricsEnabled,
  buildFilter,
  buildInterval,
  buildSessionRequestBody,
  buildRequestBody,
  buildAdvanceRequestBody,
  buildSummary,
  buildLocationMetadata,
  buildViewingExpData,
  getErrorMetric,
  buildTagGroups,
  formatPlayTime,
  getSharingState,
  getExportConfig,
  getSpiColor,
};
